/**
 * Shared API paths and route prefixes for the Configuration → Fare Media
 * reference-data screens (issuers, issuer↔organisation assignments, types).
 *
 * Backend: BACKEND/routes/v1/Configuration/* (Express), NOT the Ruby API that
 * still serves Organizations.
 *
 * RESPONSE-MESSAGE LANGUAGE: nothing to do here. A request interceptor on the
 * global axios in src/index.tsx already stamps `lang` from
 * localStorage EASY_MOBILITY_LANGUAGE onto every request, and the API
 * translates its own messages from that (BACKEND/utils/i18n.js). So the
 * "cannot delete, N cards use this" conflicts arrive already translated and go
 * straight into a toast. Do NOT append `?lang=` here — it would send the param
 * twice, which Express parses as an array.
 */

const CONFIG_BASE = '/api/v1/web_portal/configuration';

/** Absolute base for the Express API. EntityTable needs the full URL. */
export const ABT_API_BASE = process.env.REACT_APP_ABT_ALL_APIS_BASE_URL || '';

export const getCurrentLanguageCode = (): string =>
  localStorage.getItem('EASY_MOBILITY_LANGUAGE') || 'en-us';

/**
 * Relative paths — for AddEntity / EditEntity / ViewEntity, which prepend the
 * base themselves and substitute `:id`.
 */
export const CONFIG_PATHS = {
  fareMediaIssuers: `${CONFIG_BASE}/fare_media_issuers`,
  fareMediaIssuerById: `${CONFIG_BASE}/fare_media_issuers/:id`,
  fareMediaIssuersLookup: `${CONFIG_BASE}/fare_media_issuers/lookup`,

  fareMediaIssuerOrganisations: `${CONFIG_BASE}/fare_media_issuers_organisations`,
  fareMediaIssuerOrganisationById: `${CONFIG_BASE}/fare_media_issuers_organisations/:id`,

  fareMediaTypes: `${CONFIG_BASE}/fare_media_types`,
  fareMediaTypeById: `${CONFIG_BASE}/fare_media_types/:id`,

  /**
   * Organisation dropdown. Existing endpoint, deliberately reused: it already
   * returns every organisation for a super admin and only the caller's own for
   * everyone else — the same scope the assignment API enforces server-side.
   */
  organisations: '/api/v1/faremedia/get_organisations',
};

/** Absolute variants, for EntityTable's getApiUrl / deleteApiUrl / filters. */
export const CONFIG_URLS = {
  fareMediaIssuers: `${ABT_API_BASE}${CONFIG_PATHS.fareMediaIssuers}`,
  fareMediaIssuerById: `${ABT_API_BASE}${CONFIG_PATHS.fareMediaIssuerById}`,
  fareMediaIssuersLookup: `${ABT_API_BASE}${CONFIG_PATHS.fareMediaIssuersLookup}`,

  fareMediaIssuerOrganisations: `${ABT_API_BASE}${CONFIG_PATHS.fareMediaIssuerOrganisations}`,
  fareMediaIssuerOrganisationById: `${ABT_API_BASE}${CONFIG_PATHS.fareMediaIssuerOrganisationById}`,

  fareMediaTypes: `${ABT_API_BASE}${CONFIG_PATHS.fareMediaTypes}`,
  fareMediaTypeById: `${ABT_API_BASE}${CONFIG_PATHS.fareMediaTypeById}`,

  organisations: `${ABT_API_BASE}${CONFIG_PATHS.organisations}`,
};

/** Route prefixes, so a screen never hard-codes a sibling's URL. */
export const CONFIG_ROUTES = {
  fareMediaIssuers: '/configuration/fare-media-issuers',
  fareMediaIssuerOrganisations: '/configuration/fare-media-issuer-organizations',
  fareMediaTypes: '/configuration/fare-media-types',
};
